import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  companionGenerate,
  companionCommit,
  companionActivate,
  companionDelete,
  companionMatting,
} from '../../api/companionService';
import './CompanionStudio.css';

/**
 * R51 v5 — Studio compagnon & avatar.
 *
 * • Galerie des générations : chaque set (4 humeurs ou avatar) est une carte,
 *   cliquable pour réactivation/suppression. Les images s'ouvrent en grand
 *   (lightbox, navigation clavier).
 * • Génération : compagnon = 4 images du MÊME personnage (même seed, seule
 *   l'expression change) ; avatar duelliste = prompt séparé.
 * • Découpe du fond côté client avec garde-fous : si la découpe n'est pas
 *   fiable, l'image originale est conservée (jamais de personnage effacé).
 */

const MOOD_PX = 512;
const AVATAR_PX = 384;

const MOOD_LIST = [
  { key: 'happy', label: 'Joyeux' },
  { key: 'sad', label: 'Triste' },
  { key: 'fear', label: 'Peur' },
  { key: 'think', label: 'Réfléchi' },
];

// ── Découpe du fond (v3) ────────────────────────────────────────────────────
// Retourne true si keying appliqué ; false = garder l'image originale
// (garde-fou : mieux vaut un fond visible qu'un personnage effacé).
const BG_HARD = 62;
const BG_SOFT = 105;

function rgbDist(p, c) {
  const dr = p[0] - c[0];
  const dg = p[1] - c[1];
  const db = p[2] - c[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function medianPatch(d, size, x0, y0, s) {
  const rs = [], gs = [], bs = [];
  for (let y = y0; y < y0 + s; y++) {
    for (let x = x0; x < x0 + s; x++) {
      const i = (y * size + x) * 4;
      rs.push(d[i]); gs.push(d[i + 1]); bs.push(d[i + 2]);
    }
  }
  rs.sort((a, b) => a - b); gs.sort((a, b) => a - b); bs.sort((a, b) => a - b);
  const m = (rs.length / 2) | 0;
  return [rs[m], gs[m], bs[m]];
}

// 8 références (4 coins + 4 milieux de bords) ; on garde les 5 les plus
// d'accord entre elles (immunité au personnage qui touche un bord).
function bgReferences(d, size) {
  const o = 4, s = 16, e = size - 20, mid = (size / 2 - 8) | 0;
  const refs = [
    medianPatch(d, size, o, o, s), medianPatch(d, size, e, o, s),
    medianPatch(d, size, o, e, s), medianPatch(d, size, e, e, s),
    medianPatch(d, size, mid, o, s), medianPatch(d, size, mid, e, s),
    medianPatch(d, size, o, mid, s), medianPatch(d, size, e, mid, s),
  ];
  let best = null;
  for (let a = 0; a < 8; a++) for (let b = a + 1; b < 8; b++) for (let c = b + 1; c < 8; c++)
  for (let dd = c + 1; dd < 8; dd++) for (let ee = dd + 1; ee < 8; ee++) {
    const set = [refs[a], refs[b], refs[c], refs[dd], refs[ee]];
    let spread = 0;
    for (let i = 0; i < 5; i++) for (let j = i + 1; j < 5; j++) {
      const v = rgbDist(set[i], set[j]);
      if (v > spread) spread = v;
    }
    if (!best || spread < best.spread) best = { spread, set };
  }
  return best; // { spread, set }
}

function floodBackground(d, size, refs, hard) {
  const trans = new Uint8Array(size * size);
  const q = [];
  const bgDist = (x, y) => {
    const i = (y * size + x) * 4;
    let m = Infinity;
    for (const c of refs) {
      const v = rgbDist([d[i], d[i + 1], d[i + 2]], c);
      if (v < m) m = v;
    }
    return m;
  };
  const seed = (x, y) => {
    const i = y * size + x;
    if (trans[i] || bgDist(x, y) >= hard) return;
    trans[i] = 1;
    q.push(i);
  };
  for (let x = 0; x < size; x++) { seed(x, 0); seed(x, size - 1); }
  for (let y = 0; y < size; y++) { seed(0, y); seed(size - 1, y); }
  for (let h = 0; h < q.length; h++) {
    const i = q[h];
    const x = i % size;
    const y = (i - x) / size;
    if (x + 1 < size) seed(x + 1, y);
    if (x - 1 >= 0) seed(x - 1, y);
    if (y + 1 < size) seed(x, y + 1);
    if (y - 1 >= 0) seed(x, y - 1);
  }
  return trans;
}

function keyBackground(ctx, size) {
  const img = ctx.getImageData(0, 0, size, size);
  const d = img.data;
  const n = size * size;
  const best = bgReferences(d, size);
  if (best.spread > 90) return false; // fond dégradé → découpe non fiable

  let trans = floodBackground(d, size, best.set, BG_HARD);
  let opaque = 0;
  for (let i = 0; i < n; i++) if (!trans[i]) opaque++;
  if (opaque / n < 0.15) {
    // sur-découpe suspecté (personnage ≈ couleur du fond) : 2e passe sévère
    trans = floodBackground(d, size, best.set, 38);
    opaque = 0;
    for (let i = 0; i < n; i++) if (!trans[i]) opaque++;
  }
  if (opaque / n < 0.15 || opaque / n > 0.97) return false; // garde-fou : original

  const mean = [
    (best.set[0][0] + best.set[1][0] + best.set[2][0] + best.set[3][0] + best.set[4][0]) / 5,
    (best.set[0][1] + best.set[1][1] + best.set[2][1] + best.set[3][1] + best.set[4][1]) / 5,
    (best.set[0][2] + best.set[1][2] + best.set[2][2] + best.set[3][2] + best.set[4][2]) / 5,
  ];
  const bgDist = (x, y) => {
    const i = (y * size + x) * 4;
    let m = Infinity;
    for (const c of best.set) {
      const v = rgbDist([d[i], d[i + 1], d[i + 2]], c);
      if (v < m) m = v;
    }
    return m;
  };
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      if (trans[y * size + x]) {
        d[i + 3] = 0;
        continue;
      }
      const near = (x + 1 < size && trans[y * size + x + 1]) ||
        (x - 1 >= 0 && trans[y * size + x - 1]) ||
        (y + 1 < size && trans[(y + 1) * size + x]) ||
        (y - 1 >= 0 && trans[(y - 1) * size + x]);
      if (near) {
        const dd = bgDist(x, y);
        if (dd < BG_SOFT) {
          const t = 1 - (dd - BG_HARD) / (BG_SOFT - BG_HARD);
          d[i] = Math.max(0, Math.min(255, Math.round(d[i] + (d[i] - mean[0]) * t * 0.35)));
          d[i + 1] = Math.max(0, Math.min(255, Math.round(d[i + 1] + (d[i + 1] - mean[1]) * t * 0.35)));
          d[i + 2] = Math.max(0, Math.min(255, Math.round(d[i + 2] + (d[i + 2] - mean[2]) * t * 0.35)));
          d[i + 3] = Math.round(255 * (1 - t));
        }
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  return true;
}

async function jpgUrlToPngDataUrl(url, size) {
  // 1) Découpe ML côté serveur (segmentation rembg) — contours précis
  const file = url.split('/').pop();
  try {
    const blob = await companionMatting(file);
    const bmp = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    canvas.getContext('2d').drawImage(bmp, 0, 0, size, size);
    const out = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (out) {
      return await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => reject(new Error('Lecture PNG impossible'));
        fr.readAsDataURL(out);
      });
    }
  } catch (e) { /* sidecar indisponible → repli canvas ci-dessous */ }

  // 2) Repli : keying canvas local (v3) avec garde-fous
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Image générée introuvable (expirée ?)');
  const blob = await resp.blob();
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bmp, 0, 0, size, size);
  // v3 : si la découpe n'est pas fiable, on garde l'image telle quelle
  // (personnage visible, fond conservé) plutôt que de l'effacer.
  keyBackground(ctx, size);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (out) => {
        if (!out) return reject(new Error('Export PNG impossible'));
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => reject(new Error('Lecture PNG impossible'));
        fr.readAsDataURL(out);
      },
      'image/png'
    );
  });
}

// ── Petits composants ───────────────────────────────────────────────────────

function Lightbox({ items, index, onClose, onNavigate }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate(1);
      if (e.key === 'ArrowLeft') onNavigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onNavigate]);
  if (index < 0 || !items[index]) return null;
  const it = items[index];
  return (
    <div className="cs-lightbox" onClick={onClose}>
      <button className="cs-lightbox__close" onClick={onClose} aria-label="Fermer">✕</button>
      <button className="cs-lightbox__nav cs-lightbox__nav--prev" onClick={(e) => { e.stopPropagation(); onNavigate(-1); }} aria-label="Précédent">‹</button>
      <figure className="cs-lightbox__fig" onClick={(e) => e.stopPropagation()}>
        <img src={it.src} alt={it.label} />
        <figcaption>
          {it.label}
          <span className="cs-lightbox__count">{index + 1} / {items.length}</span>
        </figcaption>
      </figure>
      <button className="cs-lightbox__nav cs-lightbox__nav--next" onClick={(e) => { e.stopPropagation(); onNavigate(1); }} aria-label="Suivant">›</button>
    </div>
  );
}

function SetCard({ entry, active, kind, onOpen, onActivate, onDelete, busyId }) {
  const date = entry.createdAt ? new Date(entry.createdAt) : null;
  const imgs = kind === 'avatar'
    ? (entry.url ? [{ src: entry.url, label: 'Avatar duelliste' }] : [])
    : MOOD_LIST.filter((m) => entry.moods && entry.moods[m.key])
        .map((m) => ({ src: entry.moods[m.key], label: `Compagnon — ${m.label}` }));
  return (
    <article className={`cs-card${active ? ' cs-card--active' : ''}`}>
      {active && <span className="cs-card__badge">★ Actif</span>}
      <div className="cs-card__thumbs">
        {imgs.map((im, i) => (
          <button
            key={i}
            className="cs-card__thumb"
            onClick={() => onOpen(imgs, i)}
            title="Agrandir"
          >
            <img src={im.src} alt={im.label} loading="lazy" />
          </button>
        ))}
      </div>
      <div className="cs-card__meta">
        <span className="cs-card__date">
          {date ? date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
        </span>
        <span className="cs-card__prompt" title={entry.prompt || ''}>{entry.prompt || 'Sans description'}</span>
      </div>
      <div className="cs-card__actions">
        {!active && (
          <>
            <button className="cs-btn cs-btn--small" disabled={busyId} onClick={() => onActivate(entry.id)}>Utiliser</button>
            <button className="cs-btn cs-btn--small cs-btn--danger" disabled={busyId} onClick={() => onDelete(entry.id)}>Supprimer</button>
          </>
        )}
      </div>
    </article>
  );
}

// ── Studio ──────────────────────────────────────────────────────────────────

export default function CompanionStudio() {
  const { user, refreshUser } = useAuth();
  const [compPrompt, setCompPrompt] = useState('');
  const [avaPrompt, setAvaPrompt] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | comp | ava | keying
  const [progress, setProgress] = useState('');
  const [step, setStep] = useState(0); // 0..4 pendant la génération compagnon
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [lightbox, setLightbox] = useState({ items: [], index: -1 });
  const timerRef = useRef(null);

  const companion = user?.companion || null;
  const avatar = user?.avatar || null;
  const compHistory = (companion?.history || []).filter((e) => e && (e.moods && Object.keys(e.moods).length) || e.companionUrl);
  const avaHistory = (avatar?.history || []).filter((e) => e && e.url);
  const busy = phase === 'comp' || phase === 'ava' || phase === 'keying';

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startTimer = () => {
    setElapsed(0);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  };
  const stopTimer = () => clearInterval(timerRef.current);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Pollinations a des ratés passagers (503 en rafale) — retry par image
  const generateWithRetry = async (prompt, kind, mood, seed, attempts = 3) => {
    let lastErr;
    for (let a = 0; a < attempts; a++) {
      try {
        return await companionGenerate(prompt, kind, mood, seed);
      } catch (e) {
        lastErr = e;
        if (a < attempts - 1) await sleep(8000 * (a + 1));
      }
    }
    throw lastErr;
  };

  const handleCompanion = async () => {
    if (busy) return;
    setError(null);
    setPhase('comp');
    setStep(0);
    startTimer();
    try {
      let seed = null;
      for (let i = 0; i < MOOD_LIST.length; i++) {
        const { key: mood } = MOOD_LIST[i];
        setStep(i + 1);
        setProgress(`Humeur ${i + 1}/${MOOD_LIST.length}`);
        const gen = await generateWithRetry(compPrompt, 'companion', mood, seed);
        seed = gen.seed;
        if (typeof gen.remaining === 'number') setRemaining(gen.remaining);
        if (i < MOOD_LIST.length - 1) await sleep(4500); // anti-spam serveur 4 s
      }
      setPhase('keying');
      setProgress('Découpe des images');
      const uid = user?.id || '';
      const images = {};
      for (let i = 0; i < MOOD_LIST.length; i++) {
        const { key: mood } = MOOD_LIST[i];
        setStep(4 + i + 1);
        images[mood] = await jpgUrlToPngDataUrl(`/api/auth/companion/raw/${uid}_${seed}_${mood}.jpg`, MOOD_PX);
      }
      await companionCommit('companion', images);
      if (typeof refreshUser === 'function') await refreshUser();
      setPhase('idle');
      setStep(0);
      setProgress('');
    } catch (e) {
      setError(e.message || 'Échec de la génération');
      setPhase('idle');
      setStep(0);
      setProgress('');
    } finally {
      stopTimer();
    }
  };

  const handleAvatar = async () => {
    if (busy) return;
    setError(null);
    setPhase('ava');
    startTimer();
    try {
      setProgress('Portrait en cours');
      const gen = await generateWithRetry(avaPrompt, 'avatar');
      if (typeof gen.remaining === 'number') setRemaining(gen.remaining);
      setPhase('keying');
      setProgress('Découpe du portrait');
      const png = await jpgUrlToPngDataUrl(`/api/auth/companion/raw/${user?.id || ''}_${gen.seed}_avatar.jpg`, AVATAR_PX);
      await companionCommit('avatar', png);
      if (typeof refreshUser === 'function') await refreshUser();
      setPhase('idle');
      setProgress('');
    } catch (e) {
      setError(e.message || 'Échec de la génération');
      setPhase('idle');
      setProgress('');
    } finally {
      stopTimer();
    }
  };

  const handleActivate = async (kind, id) => {
    setBusyId(id);
    setError(null);
    try {
      await companionActivate(kind, id);
      if (typeof refreshUser === 'function') await refreshUser();
    } catch (e) {
      setError(e.message || 'Activation impossible');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (kind, id) => {
    if (!window.confirm('Supprimer définitivement cette génération ?')) return;
    setBusyId(id);
    setError(null);
    try {
      await companionDelete(kind, id);
      if (typeof refreshUser === 'function') await refreshUser();
    } catch (e) {
      setError(e.message || 'Suppression impossible');
    } finally {
      setBusyId(null);
    }
  };

  const openLightbox = (items, index) => setLightbox({ items, index });
  const navLightbox = (dir) =>
    setLightbox((lb) => ({ ...lb, index: (lb.index + dir + lb.items.length) % lb.items.length }));

  return (
    <section className="cs-studio">
      <header className="cs-studio__header">
        <h3 className="cs-studio__title">🧙 Studio compagnon &amp; avatar</h3>
        {typeof remaining === 'number' && (
          <span className="cs-quota">{remaining} image{remaining > 1 ? 's' : ''} restante{remaining > 1 ? 's' : ''} aujourd'hui</span>
        )}
      </header>

      {/* ── Galerie compagnons ── */}
      {compHistory.length > 0 && (
        <div className="cs-section">
          <h4 className="cs-section__title">Mes compagnons</h4>
          <div className="cs-gallery">
            {compHistory.map((e) => (
              <SetCard
                key={e.id}
                entry={e}
                kind="companion"
                active={companion?.id === e.id}
                onOpen={openLightbox}
                onActivate={(id) => handleActivate('companion', id)}
                onDelete={(id) => handleDelete('companion', id)}
                busyId={busyId}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Galerie avatars ── */}
      {avaHistory.length > 0 && (
        <div className="cs-section">
          <h4 className="cs-section__title">Mes avatars</h4>
          <div className="cs-gallery">
            {avaHistory.map((e) => (
              <SetCard
                key={e.id}
                entry={e}
                kind="avatar"
                active={avatar?.id === e.id}
                onOpen={openLightbox}
                onActivate={(id) => handleActivate('avatar', id)}
                onDelete={(id) => handleDelete('avatar', id)}
                busyId={busyId}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Génération ── */}
      <div className="cs-section cs-section--gen">
        <h4 className="cs-section__title">Nouvelle génération</h4>

        <div className="cs-gen">
          <div className="cs-gen__field">
            <label className="cs-gen__label" htmlFor="cs-prompt-comp">Le compagnon — 4 images du même personnage (joyeux, triste, peur, réfléchi)</label>
            <textarea
              id="cs-prompt-comp"
              className="cs-gen__input"
              value={compPrompt}
              onChange={(e) => setCompPrompt(e.target.value.slice(0, 200))}
              placeholder="Ex : petit dragon bleu cristallin, écailles scintillantes, queue en pointe"
              rows={2}
              maxLength={200}
              disabled={busy}
            />
            <button
              type="button"
              className="cs-btn cs-btn--primary"
              onClick={handleCompanion}
              disabled={busy || compPrompt.trim().length < 3}
            >
              {phase === 'comp' || (phase === 'keying' && step >= 5) ? `${progress}… ${elapsed}s` : 'Générer le compagnon'}
            </button>
          </div>

          <div className="cs-gen__field">
            <label className="cs-gen__label" htmlFor="cs-prompt-ava">Ton duelliste — l'avatar du personnage JOUEUR</label>
            <textarea
              id="cs-prompt-ava"
              className="cs-gen__input"
              value={avaPrompt}
              onChange={(e) => setAvaPrompt(e.target.value.slice(0, 200))}
              placeholder="Ex : jeune rôdeuse elfe, cape émeraude, cheveux argentés"
              rows={2}
              maxLength={200}
              disabled={busy}
            />
            <button
              type="button"
              className="cs-btn"
              onClick={handleAvatar}
              disabled={busy || avaPrompt.trim().length < 3}
            >
              {phase === 'ava' || (phase === 'keying' && step < 5 && step > 0) ? `${progress}… ${elapsed}s` : 'Générer mon avatar'}
            </button>
          </div>
        </div>

        {/* Stepper de progression */}
        {busy && phase === 'comp' && (
          <div className="cs-stepper">
            {MOOD_LIST.map((m, i) => (
              <div key={m.key} className={`cs-stepper__step${step > i ? ' cs-stepper__step--done' : ''}${step === i + 1 ? ' cs-stepper__step--current' : ''}`}>
                <span className="cs-stepper__dot" />
                <span className="cs-stepper__label">{m.label}</span>
              </div>
            ))}
          </div>
        )}

        <p className="cs-hint">
          Les 4 humeurs gardent le même personnage et la même tenue (1 à 2 min).
          {' '}<span className="cs-gen__count">{compPrompt.length}/200</span>
        </p>
      </div>

      {error && <p className="cs-error">{error}</p>}

      {lightbox.index >= 0 && (
        <Lightbox
          items={lightbox.items}
          index={lightbox.index}
          onClose={() => setLightbox({ items: [], index: -1 })}
          onNavigate={navLightbox}
        />
      )}
    </section>
  );
}
